import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { ticket, user, stato_ticket } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { AxiosError } from 'axios'
import notify from 'devextreme/ui/notify'
import { StatoTicket } from '@/routes/tickets/TicketEditor.enums'
import { getTicketNoteQueryKey } from '@/routes/tickets/notes/ticketNotes'
import { getTicketDetailQueryKey } from '@/routes/tickets/ticketDetail'

interface TicketMutationParams {
  ticket: ticket
}

export type AssignToMutationParams = {
  ticketRecordId: number
  assigneeUser: user
  assignedStato?: stato_ticket // Optional: the new state object for optimistic update
}

interface NotificationMutationParams {
  ticket: ticket
}

const refreshTicketPageData = (
  queryClient: ReturnType<typeof useQueryClient>,
  clientId: string | undefined,
  ticketInfo: { id?: number | null; ded_Dis?: string | null },
) => {
  queryClient.invalidateQueries({ queryKey: ['tickets', 'active', clientId] })

  if (ticketInfo.id != null) {
    queryClient.invalidateQueries({ queryKey: ['ticket', ticketInfo.id] })
    queryClient.invalidateQueries({ queryKey: getTicketDetailQueryKey(ticketInfo.id) })
    queryClient.invalidateQueries({ queryKey: getTicketNoteQueryKey(ticketInfo.id) })
  }
}

const broadcastTicketUpdated = (ticketRecordId?: number | null) => {
  if (typeof window === 'undefined' || ticketRecordId == null) return

  const channel = new BroadcastChannel(`ticket-${ticketRecordId}`)
  channel.postMessage({ type: 'ticket-updated' })
  channel.close()
}

export const useTicketTakeChargeMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { service } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: TicketMutationParams) => {
      return await service.ticket(ticket.id).TakeCharge()
    },
    onSuccess: (_data, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify('Ticket preso in carico con successo', 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `Errore durante la presa in carico del ticket: ${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}

export const useTicketReleaseMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { service } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: TicketMutationParams) => {
      return await service.ticket(ticket.id).Release()
    },
    onSuccess: (_data, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify('Ticket rilasciato con successo', 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `Errore durante il rilascio del ticket: ${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}

export const useTicketRemoveAssigneeMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { service } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: TicketMutationParams) => {
      return await service.ticket(ticket.id).RemoveAssignee()
    },
    onSuccess: (_data, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify('Assegnazione rimossa con successo', 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `Errore durante la rimozione dell'assegnazione: ${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}

export const useTicketAssignToMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { client } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticketRecordId, assigneeUser }: AssignToMutationParams) => {
      console.log('Assegnando ticket a:', assigneeUser.id)
      return await client.post(
        `api/tickets/${ticketRecordId}/assign`,
        {
          userId: assigneeUser.id,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
    },
    onMutate: async (variables: AssignToMutationParams) => {
      const { ticketRecordId, assigneeUser, assignedStato } = variables
      const queryKey = ['tickets', 'active', clientId]

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey })

      // Snapshot the previous value
      const previousTickets = queryClient.getQueryData<ticket[]>(queryKey)

      // Optimistically update to the new value
      if (previousTickets) {
        queryClient.setQueryData<ticket[]>(queryKey, (oldData) =>
          (oldData || []).map((item) =>
            item.id === ticketRecordId
              ? {
                  ...item,
                  assegnato: assigneeUser,
                  stato: assignedStato || ({ id: StatoTicket.ASSEGNATO, nome: 'Assegnato' } as stato_ticket),
                  // Potentially update incaricato as well if business logic dictates assignee becomes incaricato
                  // incaricato: assigneeUser, // Example: if assigning also means taking charge
                }
              : item,
          ),
        )
      }
      // Return a context object with the snapshotted value
      return { previousTickets, queryKey }
    },
    onError: (err, _variables, context) => {
      notify(
        `Errore durante l'assegnazione del ticket: ${err instanceof AxiosError ? (err.response?.data?.error?.exceptionMessage ?? err.response?.data?.error?.message ?? err.message) : (err as Error).message}`,
        'error',
        3000,
      )
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousTickets) {
        queryClient.setQueryData(context.queryKey, context.previousTickets)
      }
    },
    onSettled: (_data, _error, variables) => {
      // Always refetch after error or success:
      refreshTicketPageData(queryClient, clientId, {
        id: variables.ticketRecordId,
      })
      broadcastTicketUpdated(variables.ticketRecordId)
    },
    onSuccess: () => {
      notify('Ticket assegnato con successo', 'success', 3000)
    },
  })
}

export const useTicketReopenMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { service } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: TicketMutationParams) => {
      return await service.ticket(ticket.id).Reopen()
    },
    onSuccess: (_data, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify('Ticket riaperto con successo', 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `Errore durante la riapertura del ticket: ${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}

export const useTicketCancelMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { service } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: TicketMutationParams) => {
      return await service.ticket(ticket.id).Cancel()
    },
    onSuccess: (_data, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify('Ticket eliminato con successo', 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `Errore durante l'eliminazione del ticket: ${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}

export const useTicketSendClosureNoticeMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { client } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: NotificationMutationParams) => {
      return await client.put(`/api/tickets/${ticket.id}/notices/closure`, null, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
    },
    onSuccess: (response, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify(`${response.data}`, 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}

export const useTicketSendContactNoticeMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { client } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: NotificationMutationParams) => {
      return await client.put(`/api/tickets/${ticket.id}/notices/contact`, null, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
    },
    onSuccess: (response, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify(`${response.data}`, 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}

export const useTicketSendSupportExpiredNoticeMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { client } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: NotificationMutationParams) => {
      return await client.post(`/api/tickets/${ticket.id}/notices/support-expired`, null, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
    },
    onSuccess: (response, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify(`${response.data}`, 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}

export const useTicketSendSupportSuspendedNoticeMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { client } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: NotificationMutationParams) => {
      return await client.post(`/api/tickets/${ticket.id}/notices/support-suspended`, null, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
    },
    onSuccess: (response, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify(`${response.data}`, 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}

export const useTicketSendOtherVendorNoticeMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { client } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: NotificationMutationParams) => {
      return await client.post(`/api/tickets/${ticket.id}/notices/other-vendor`, null, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
    },
    onSuccess: (response, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify(`${response.data}`, 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}

export const useTicketIncrementCallNumberMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { service } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: TicketMutationParams) => {
      return await service.ticket(ticket.id).IncrementCallNumber()
    },
    onSuccess: (_data, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify('Chiamate incrementate con successo', 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `Errore durante l'incremento delle chiamate: ${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}

export const useTicketSetUrgentMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { service } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: TicketMutationParams) => {
      return await service.ticket(ticket.id).SetUrgent()
    },
    onSuccess: (_data, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify('Urgenza impostata con successo', 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `Errore durante l'impostazione dell'urgenza: ${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}

export const useTicketSetWaitMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { service } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: TicketMutationParams) => {
      return await service.ticket(ticket.id).SetWait()
    },
    onSuccess: (_data, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify('Ticket impostato in attesa con successo', 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `Errore durante l'impostazione in attesa del ticket: ${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}
export const useTicketClearWaitMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { service } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: TicketMutationParams) => {
      return await service.ticket(ticket.id).ClearWait()
    },
    onSuccess: (_data, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify('Ticket rimosso da attesa con successo', 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `Errore durante la rimozione dall'attesa del ticket: ${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}
export const useTicketSetActivityRequestMutation = (clientId?: string) => {
  const queryClient = useQueryClient()
  const { service } = useQsAdminApiManager()

  return useMutation({
    mutationFn: async ({ ticket }: TicketMutationParams) => {
      return await service.ticket(ticket.id).SetActivityRequest()
    },
    onSuccess: (_data, variables) => {
      refreshTicketPageData(queryClient, clientId, variables.ticket)
      broadcastTicketUpdated(variables.ticket.id)
      notify(`Ticket impostato come 'RICHIESTA DI ATTIVITA'' con successo`, 'success', 3000)
    },
    onError: (error: AxiosError<any, any>) => {
      notify(
        `Errore durante l'impostazione in 'RICHIESTA DI ATTIVITA'' del ticket: ${error.response?.data?.error?.exceptionMessage ?? error.response?.data?.error?.message ?? error.message}`,
        'error',
        3000,
      )
    },
  })
}
